import {
  Button,
  Field,
  Input,
  RichTextarea,
  Select,
  Textarea,
} from '@payaca/ambrosia';
import ValidatedForm, {
  TRenderFormContents,
} from '@payaca/components/validatedForm/ValidatedForm';
import { FC, useCallback, useMemo, type JSX } from 'react';

import { useGetCustomFieldVariables } from '@/hooks/useCustomFieldsForRole';
import { useAccount } from '@/api/rest/users/useAccount';

import {
  getAllowEmptyValidator,
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
  getNumericalRangeFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import {
  type EditableStoredPaymentSchedule,
  fromEditableStoredPaymentSchedule,
  getEditableStoredPaymentScheduleFieldValidator,
} from '@payaca/helpers/editableStoredPaymentSchedule';
import { isPaymentScheduleValid } from '@payaca/helpers/paymentScheduleHelper';
import {
  EntityTemplatePlaceholderVariables,
  PublicEntityTemplate,
} from '@payaca/types/entity-templates';
import { isNullish } from '@payaca/utilities/guards';
import ProposalTemplatePaymentScheduleEditor from './ProposalTemplatePaymentScheduleEditor';
import { ProposalTemplateGrantsControl } from './ProposalTemplateGrants';

type ProposalTemplate = Omit<PublicEntityTemplate<'proposal'>, 'publicId'>;

/**
 * Form-bound shape of a proposal template. Differs from the persisted shape
 * because Ambrosia `Input` emits strings from `e.target.value`, so any
 * number-typed field the user has touched is actually a string in form
 * state until we coerce on save.
 */
type ProposalTemplateFormState = Omit<ProposalTemplate, 'data'> & {
  data: Omit<ProposalTemplate['data'], 'paymentSchedule' | 'validForDays'> & {
    paymentSchedule?: EditableStoredPaymentSchedule | null;
    validForDays?: number | string | null;
  };
};

type Props = {
  proposalTemplate?: Omit<PublicEntityTemplate<'proposal'>, 'publicId'>;
  onRequestSave?: (
    proposalTemplate: Omit<PublicEntityTemplate<'proposal'>, 'publicId'>
  ) => void;
  allowModifyTemplateNameDescription?: boolean;
};
const CreateEditProposalTemplateControl: FC<Props> = ({
  proposalTemplate,
  onRequestSave,
  allowModifyTemplateNameDescription = true,
}: Props): JSX.Element => {
  const projectCustomFieldVariables =
    useGetCustomFieldVariables<EntityTemplatePlaceholderVariables>(
      EntityTemplatePlaceholderVariables.PROJECT_CUSTOMFIELD_VALUE,
      'Project custom field'
    );
  const companyCustomFieldVariables =
    useGetCustomFieldVariables<EntityTemplatePlaceholderVariables>(
      EntityTemplatePlaceholderVariables.ACCOUNT_CUSTOMFIELD_VALUE,
      'Company custom field',
      { role: 'account' }
    );

  const editorVariables = useMemo(() => {
    const ev: {
      name: string;
      displayName: string;
    }[] = [
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_NAME,
        displayName: 'Customer name',
      },
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_PRIMARYCONTACT_NAME,
        displayName: 'Primary contact name',
      },
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_PRIMARYCONTACT_FIRSTNAME,
        displayName: 'Primary contact first name',
      },
      {
        name: EntityTemplatePlaceholderVariables.PROJECT_SITEADDRESSES_ARRAYSTRING,
        displayName: 'Project site address(es)',
      },
    ];

    [...projectCustomFieldVariables, ...companyCustomFieldVariables].forEach(
      (variable) => {
        ev.push({
          name: variable.variableName,
          displayName: variable.displayName,
        });
      }
    );

    return ev;
  }, [projectCustomFieldVariables, companyCustomFieldVariables]);

  const fieldValidators = useMemo(() => {
    return {
      name: [
        getIsRequiredFieldValidator(),
        getLengthFieldValidator({ min: 0, max: 60 }),
      ],
      description: [getLengthFieldValidator({ min: 0, max: 255 })],
      'data.validForDays': [
        getAllowEmptyValidator(
          getNumericalRangeFieldValidator(
            -0.01,
            undefined,
            undefined,
            'This field must be a positive number'
          )
        ),
      ],
      'data.paymentSchedule': [
        getEditableStoredPaymentScheduleFieldValidator(),
      ],
      'data.summaryMessages[0].message': [
        getLengthFieldValidator({ min: 0, max: 125 }),
      ],
    };
  }, []);

  const account = useAccount();

  const initialFormState = useMemo<ProposalTemplateFormState>(() => {
    const { paymentSchedule, ...data } = proposalTemplate?.data ?? {};
    return {
      name: proposalTemplate?.name ?? '',
      description: proposalTemplate?.description ?? '',
      entityType: 'proposal',
      data: {
        validForDays: account?.propositionValidForDays,
        ...data,
        paymentSchedule: paymentSchedule ?? null,
        summaryMessages: proposalTemplate?.data.summaryMessages || [
          {
            level: 'alert',
            message: '',
          },
        ],
      },
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'account.propositionValidForDays'
  }, [proposalTemplate]);

  const saveTemplate = (formState: ProposalTemplateFormState) => {
    const paymentSchedule = formState.data.paymentSchedule
      ? fromEditableStoredPaymentSchedule(formState.data.paymentSchedule)
      : null;
    const paymentScheduleToSave =
      paymentSchedule?.stages.length && isPaymentScheduleValid(paymentSchedule)
        ? paymentSchedule
        : null;

    const templateToSave: Omit<PublicEntityTemplate<'proposal'>, 'publicId'> = {
      ...formState,
      data: {
        ...formState.data,
        paymentSchedule: paymentScheduleToSave,
        validForDays: isNullish(formState.data.validForDays)
          ? formState.data.validForDays
          : Number(formState.data.validForDays),
      },
    };

    onRequestSave?.(templateToSave);
  };

  const renderFormContents: TRenderFormContents<ProposalTemplateFormState> =
    useCallback(
      (
        isValid,
        formState,
        validationState,
        touchedState,
        onFieldChange,
        onFieldTouch
      ) => {
        return (
          <>
            {allowModifyTemplateNameDescription && (
              <>
                <Field.Legacy
                  name="name"
                  validationState={
                    !validationState?.['name']?.isValid &&
                    touchedState?.['name']
                      ? {
                          isValid: false,
                          validationMessages: validationState?.['name']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Template name</Field.Label>
                  <Input
                    value={formState.name ?? ''}
                    onChange={(value) => onFieldChange({ name: value })}
                    onBlur={() => onFieldTouch?.('name')}
                  />
                </Field.Legacy>
                <Field.Legacy
                  name="description"
                  validationState={
                    !validationState?.['description']?.isValid &&
                    touchedState?.['description']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['description']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Template description</Field.Label>
                  <Textarea
                    value={formState.description ?? ''}
                    onChange={(value) => onFieldChange({ description: value })}
                    onBlur={() => onFieldTouch?.('description')}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.type">
                  <Field.Label>Proposal type</Field.Label>
                  <Select
                    className={'max-w-52'}
                    value={formState.data.type ?? undefined}
                    onChange={(value) =>
                      onFieldChange({ ['data.type']: value })
                    }
                    options={[
                      { value: 'ESTIMATE', label: 'Estimate' },
                      { value: 'QUOTE', label: 'Quote' },
                    ]}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.introduction">
                  <Field.Label>Intro</Field.Label>
                  <RichTextarea
                    value={formState.data.introduction ?? null}
                    onChange={(value) =>
                      onFieldChange({ ['data.introduction']: value })
                    }
                    variables={editorVariables}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.notes">
                  <Field.Label>Notes</Field.Label>
                  <RichTextarea
                    value={formState.data.notes ?? null}
                    onChange={(value) =>
                      onFieldChange({ ['data.notes']: value })
                    }
                    variables={editorVariables}
                  />
                </Field.Legacy>
                <Field.Legacy
                  name="data.validForDays"
                  validationState={
                    !validationState?.['data.validForDays']?.isValid &&
                    touchedState?.['data.validForDays']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['data.validForDays']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Valid for (days)</Field.Label>

                  <Input
                    className={'max-w-52'}
                    value={formState.data.validForDays ?? ''}
                    onChange={(value) =>
                      onFieldChange({ ['data.validForDays']: value })
                    }
                    onBlur={() => onFieldTouch?.('data.validForDays')}
                    // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
                    type="number"
                    step={1}
                  />
                </Field.Legacy>

                <ProposalTemplatePaymentScheduleEditor
                  paymentSchedule={
                    formState.data.paymentSchedule ?? {
                      stages: [],
                      explainer: null,
                    }
                  }
                  onChange={(change) =>
                    onFieldChange({ ['data.paymentSchedule']: change })
                  }
                />
                <ProposalTemplateGrantsControl
                  grantIds={formState.data.grantIds ?? []}
                  hasPaymentSchedule={
                    !!formState.data.paymentSchedule?.stages?.length
                  }
                  onChange={(grantIds) =>
                    onFieldChange({ ['data.grantIds']: grantIds })
                  }
                />
                <Field.Legacy
                  name="data.summaryMessages[0].message"
                  validationState={
                    !validationState?.['data.summaryMessages[0].message']
                      ?.isValid &&
                    touchedState?.['data.summaryMessages[0].message']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['data.summaryMessages[0].message']
                              ?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Payment summary alert</Field.Label>
                  <p className="mb-2">
                    This text will show immediately below the &#34;Total payable
                    amount&#34; in the summary box on the right.
                  </p>
                  <Input
                    value={formState.data.summaryMessages?.[0]?.message ?? ''}
                    onChange={(value) =>
                      onFieldChange({
                        ['data.summaryMessages[0].message']: value,
                      })
                    }
                    onBlur={() =>
                      onFieldTouch?.('data.summaryMessages[0].message')
                    }
                  />
                </Field.Legacy>
              </>
            )}
            <div className="actions-container">
              <Button
                onClick={() => saveTemplate(formState)}
                disabled={!isValid}
              >
                {proposalTemplate ? 'Save' : 'Create'} proposal template
              </Button>
            </div>
          </>
        );
      },
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'saveTemplate'
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: onRequestSave
      [
        proposalTemplate,
        onRequestSave,
        allowModifyTemplateNameDescription,
        editorVariables,
      ]
    );

  return (
    <div className="create-edit-scheduled-event-template-control">
      <ValidatedForm
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
        initialFormState={initialFormState}
      ></ValidatedForm>
    </div>
  );
};
export default CreateEditProposalTemplateControl;
